import { useCallback, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useProfile } from "@/hooks/useProfile";
import type { License } from "@/pages/licenses/hooks/useMyLicenses";

interface UseRenewalRequestResult {
  submit: (license: License, note: string) => Promise<void>;
  submitting: boolean;
}

/**
 * 客户侧「一键申请续期」：在 license_applications 中写入一条续期申请，
 * 通过 renew_license_id 关联到已有许可证，管理员审批通过后顺延该许可证有效期。
 */
export function useRenewalRequest(): UseRenewalRequestResult {
  const { user } = useCurrentUser();
  const { profile } = useProfile();
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    async (license: License, note: string) => {
      if (!user) throw new Error("请先登录后再申请续期");
      setSubmitting(true);
      try {
        const trimmed = note.trim();
        const payload = {
          user_id: user.id,
          applicant_name:
            profile?.full_name ||
            (user.user_metadata?.full_name as string | undefined) ||
            user.email?.split("@")[0] ||
            "用户",
          email: profile?.email || user.email || "",
          company: profile?.company ?? null,
          product_name: license.product_name,
          product_version: null,
          edition: license.edition,
          seat_count: license.seat_count ?? 1,
          machine_code: null,
          use_case: trimmed || `【续期申请】为许可证 LH-••••-${license.license_key.slice(-4)} 申请续期`,
          status: "pending",
          renew_license_id: license.id,
        };

        const { error } = await supabase.from("license_applications").insert(payload);
        if (error) throw error;
      } finally {
        setSubmitting(false);
      }
    },
    [profile, user],
  );

  return { submit, submitting };
}